import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, User, Phone, ArrowLeft, AlertCircle, Loader2, CheckCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';

const RegisterPage = () => {
  const { register } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedPlan = searchParams.get('plan') || 'pos';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    plan: selectedPlan,
    acceptTerms: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError(language === 'es' ? 'Las contraseñas no coinciden' : 'Passwords do not match');
      return;
    }

    if (!formData.acceptTerms) {
      setError(language === 'es' ? 'Debes aceptar los términos y condiciones' : 'You must accept the terms and conditions');
      return;
    }

    setLoading(true);

    try {
      const result = await register({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        plan: formData.plan,
        language: language,
      });
      
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-8" data-testid="register-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center"
        >
          <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-[#22c55e]" />
          </div>
          <h2 className="text-3xl font-bold text-[#1e3a5f] mb-4">
            {language === 'es' ? '¡Registro Exitoso!' : 'Registration Successful!'}
          </h2>
          <p className="text-slate-600 mb-8">
            {language === 'es' 
              ? 'Hemos enviado un código de verificación a tu email. Por favor, verifica tu cuenta para continuar.'
              : 'We sent a verification code to your email. Please verify your account to continue.'}
          </p>

          <Link
            to={`/verify-email?email=${encodeURIComponent(formData.email)}`}
            className="vf-btn-primary inline-flex items-center justify-center gap-2"
          >
            {language === 'es' ? 'Verificar Email' : 'Verify Email'}
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex" data-testid="register-page">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1e3a5f] p-12 flex-col justify-between">
        <div>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#22c55e] rounded-sm flex items-center justify-center">
              <span className="text-white font-bold text-xl">V</span>
            </div>
            <span className="text-xl font-bold text-white tracking-tight">
              VendFactura
            </span>
          </Link>
        </div>
        
        <div className="space-y-6">
          <h1 className="text-4xl font-bold text-white leading-tight">
            {language === 'es' 
              ? 'Empieza a facturar con confianza'
              : 'Start invoicing with confidence'}
          </h1>
          <p className="text-white/70 text-lg">
            {language === 'es'
              ? '14 días de prueba gratis. Sin tarjeta requerida.'
              : '14-day free trial. No credit card required.'}
          </p>
          
          {/* Plan highlight */}
          <div className="mt-8 p-6 bg-white/10 rounded-sm">
            <p className="text-white/60 text-sm uppercase tracking-wider mb-2">
              {language === 'es' ? 'Plan seleccionado' : 'Selected plan'}
            </p>
            <p className="text-2xl font-bold text-white">
              {formData.plan === 'invoice' 
                ? (language === 'es' ? 'Facturas - €4.99/mes' : 'Invoices - €4.99/mo')
                : (language === 'es' ? 'TPV Tienda - €19.99/mes' : 'Shop POS - €19.99/mo')
              }
            </p>
          </div>
        </div>
        
        <div className="text-white/50 text-sm">
          © 2025 VendFactura
        </div>
      </div>

      {/* Right Side - Register Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md py-8"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#1e3a5f] rounded-sm flex items-center justify-center">
                <span className="text-white font-bold text-xl">V</span>
              </div>
              <span className="text-xl font-bold text-[#1e3a5f] tracking-tight">
                VendFactura
              </span>
            </Link>
          </div>

          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-600 hover:text-[#1e3a5f] mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            {language === 'es' ? 'Volver' : 'Back'}
          </Link>

          <h2 className="text-3xl font-bold text-[#1e3a5f] mb-2">
            {language === 'es' ? 'Crear Cuenta' : 'Create Account'}
          </h2>
          <p className="text-slate-600 mb-8">
            {language === 'es' 
              ? '¿Ya tienes cuenta?' 
              : 'Already have an account?'}
            {' '}
            <Link to="/login" className="text-[#22c55e] font-medium hover:underline">
              {language === 'es' ? 'Inicia sesión' : 'Log in'}
            </Link>
          </p>

          {/* Plan Selector */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-[#1e3a5f] mb-2">
              {language === 'es' ? 'Selecciona tu Plan' : 'Select your Plan'}
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, plan: 'invoice' }))}
                className={`p-4 border-2 rounded-sm text-left transition-colors ${
                  formData.plan === 'invoice' 
                    ? 'border-[#22c55e] bg-green-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <p className="font-semibold text-[#1e3a5f]">
                  {language === 'es' ? 'Facturas' : 'Invoices'}
                </p>
                <p className="text-sm text-slate-600">€4.99/mes</p>
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, plan: 'pos' }))}
                className={`p-4 border-2 rounded-sm text-left transition-colors ${
                  formData.plan === 'pos' 
                    ? 'border-[#22c55e] bg-green-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <p className="font-semibold text-[#1e3a5f]">
                  {language === 'es' ? 'TPV Tienda' : 'Shop POS'}
                </p>
                <p className="text-sm text-slate-600">€19.99/mes</p>
              </button>
            </div>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-red-50 border border-red-200 rounded-sm flex items-center gap-3"
            >
              <AlertCircle className="w-5 h-5 text-red-500" />
              <span className="text-red-700">{error}</span>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-[#1e3a5f] mb-2">
                {language === 'es' ? 'Nombre Completo' : 'Full Name'}
              </label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="vf-input w-full pl-12 pr-4 py-3"
                  placeholder={language === 'es' ? 'Tu nombre' : 'Your name'}
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1e3a5f] mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="vf-input w-full pl-12 pr-4 py-3"
                  placeholder="tu@email.com"
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1e3a5f] mb-2">
                {language === 'es' ? 'Teléfono (opcional)' : 'Phone (optional)'}
              </label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="vf-input w-full pl-12 pr-4 py-3"
                  placeholder="+34 600 000 000"
                  data-testid="register-phone-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1e3a5f] mb-2">
                {language === 'es' ? 'Contraseña' : 'Password'}
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={8}
                  className="vf-input w-full pl-12 pr-12 py-3"
                  placeholder="••••••••"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1e3a5f] mb-2">
                {language === 'es' ? 'Confirmar Contraseña' : 'Confirm Password'}
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className="vf-input w-full pl-12 pr-4 py-3"
                  placeholder="••••••••"
                  data-testid="register-confirm-password-input"
                />
              </div>
            </div>

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                name="acceptTerms"
                checked={formData.acceptTerms}
                onChange={handleChange}
                className="w-5 h-5 mt-0.5 border-2 border-slate-300"
                data-testid="register-terms-checkbox"
              />
              <label className="text-sm text-slate-600">
                {language === 'es' ? 'Acepto los ' : 'I accept the '}
                <Link to="/terms" className="text-[#22c55e] hover:underline">
                  {language === 'es' ? 'términos y condiciones' : 'terms and conditions'}
                </Link>
                {language === 'es' ? ' y la ' : ' and the '}
                <Link to="/privacy" className="text-[#22c55e] hover:underline">
                  {language === 'es' ? 'política de privacidad' : 'privacy policy'}
                </Link>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="vf-btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                language === 'es' ? 'Crear Cuenta' : 'Create Account'
              )}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default RegisterPage;
